"use client";

import React, { useEffect, useState } from "react";
import { Star, ShoppingCart } from "lucide-react";
import { Product, Variant } from "@/packages/types/products";
import { useCart } from "@/packages/context/CartContext";
import { useWishlist } from "@/packages/context/wishlistContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "../common/NextImage";
import SelectField from "../common/SelectBox";
import Price from "../common/Price";
import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";

export interface ProductCardV2Props {
  product: Product & { variants?: Variant[] };
  onQuickView: () => void;
  className?: string;
}

const ProductCardV2: React.FC<ProductCardV2Props> = ({
  product,
  onQuickView,
  className = "",
}) => {
  const countryName = useResolvedCountryName();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { fastPush } = useFastRouter();

  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);

  const [imgSrc, setImgSrc] = useState(
    product?.images?.[0]?.thumbnailUrl ||
      product?.images?.[0]?.imageUrl ||
      product?.thumbnail ||
      "/assets/site/images/default.png"
  );

  // Set default selected variant
  useEffect(() => {
    if (product.variantStatus && product.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0]);
    } else {
      setSelectedVariant(null);
    }
  }, [product]);

  // Update wishlist status
  useEffect(() => {
    setIsWishlisted(isInWishlist(product.id));
  }, [product, isInWishlist]);

  // Dynamically update image source based on selected variant's imageId or product_image
  useEffect(() => {
    if (product.variantStatus && selectedVariant) {
      const variantImg =
        selectedVariant.product_image?.thumbnailUrl ||
        selectedVariant.product_image?.imageUrl;

      const matchedImage = product.images?.find(
        (img) => img.id === selectedVariant.imageId
      );

      if (variantImg) {
        setImgSrc(variantImg);
      } else if (matchedImage) {
        setImgSrc(matchedImage.thumbnailUrl || matchedImage.imageUrl);
      } else if (product.images && product.images.length > 0) {
        setImgSrc(product.images[0].thumbnailUrl || product.images[0].imageUrl);
      }
    } else {
      const fallbackImg =
        product?.images?.[0]?.thumbnailUrl ||
        product?.images?.[0]?.imageUrl ||
        product?.thumbnail ||
        "/assets/site/images/default.png";
      setImgSrc(fallbackImg);
    }
  }, [product, selectedVariant]);

  const handleCardClick = () => {
    fastPush(`/products/${product.slug}`);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedStock <= 0) return;
    if (product?.variantStatus) {
      if (selectedVariant) {
        addToCart(product, 1, selectedVariant);
      } else {
        onQuickView();
      }
    } else {
      addToCart(product, 1);
    }
  };

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleWishlist(product);
    setIsWishlisted(!isWishlisted);
  };

  // Determine stock & pricing based on selected variant or root product
  const selectedStock = selectedVariant ? Number(selectedVariant.stock ?? product.stock ?? 0) : Number(product.stock ?? 0);
  const salePrice = selectedVariant ? Number(selectedVariant.salePrice) : Number(product.salePrice);
  const comparePrice = selectedVariant
    ? selectedVariant.comparePrice
      ? Number(selectedVariant.comparePrice)
      : null
    : product.comparePrice
    ? Number(product.comparePrice)
    : null;
  const hasDiscount = Boolean(comparePrice && comparePrice > salePrice);

  return (
    <div
      onClick={handleCardClick}
      className={`group flex flex-col bg-white border border-slate-100 transition-all duration-300 hover:shadow-md cursor-pointer relative overflow-hidden h-full ${className}
        ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card} 
        ${HEADER_V2_DESIGN_SYSTEM.elevation.card}`}
    >
      {/* Image Wrapper */}
      <div className="relative aspect-square w-full bg-[#F9FAFB] overflow-hidden flex items-center justify-center p-4">
        <NextImage
          src={imgSrc?.trim() || "/assets/site/images/default.png"}
          alt={
            product.title
              ? `${product.title}${countryName ? ` in ${countryName}` : ""}`
              : "Product Image"
          }
          fill
          sizes="(max-width: 640px) 45vw, 20vw"
          className="object-contain p-4 transition-transform duration-500 ease-out group-hover:scale-105"
          onError={() => setImgSrc("/assets/site/images/default.png")}
        />

        {/* Stock & Real-Data Discount Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1 z-10 pointer-events-none">
          {/* Out of Stock */}
          {selectedStock <= 0 ? (
            <span className="bg-rose-600 text-white text-[9px] font-extrabold tracking-wider uppercase px-2 py-0.5 rounded-md shadow-xs">
              Out of Stock
            </span>
          ) : (
            <>
              {/* Discount Badge */}
              {hasDiscount && comparePrice != null ? (
                <span className="bg-[var(--brand-accent)] text-white text-[9px] font-extrabold tracking-wider uppercase px-2 py-0.5 rounded-md shadow-xs">
                  SAVE {Math.round(((comparePrice - salePrice) / comparePrice) * 100)}%
                </span>
              ) : product.specialDiscountStatus && product.specialDiscount?.value ? (
                <span className="bg-[var(--brand-accent)] text-white text-[9px] font-extrabold tracking-wider uppercase px-2 py-0.5 rounded-md shadow-xs">
                  SAVE {Math.round(Number(product.specialDiscount.value))}%
                </span>
              ) : null}

              {/* Low Stock Indicator */}
              {selectedStock > 0 && selectedStock <= 5 && (
                <span className="bg-amber-500 text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md shadow-xs">
                  Only {selectedStock} Left
                </span>
              )}
            </>
          )}
        </div>

        {/* Wishlist Button Overlay */}
        <button
          onClick={handleWishlistClick}
          className="absolute top-2.5 right-2.5 z-10 p-1.5 rounded-full bg-white/80 hover:bg-white text-slate-600 hover:text-red-500 shadow-sm transition-colors"
          aria-label="Add to wishlist"
        >
          <svg
            className={`w-4 h-4 ${isWishlisted ? "fill-red-500 text-red-500" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4 justify-between">
        <div>
          {/* Rating */}
          {product.averageRating > 0 && (
            <div className="mb-2 flex items-center gap-1">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${
                      i < Math.floor(product.averageRating)
                        ? "text-amber-400 fill-amber-400"
                        : "text-slate-200"
                    }`}
                  />
                ))}
              </div>
              <span className="text-[10px] text-slate-500 font-medium">
                ({product.totalRating || 0})
              </span>
            </div>
          )}

          {/* Name */}
          <h3
            className={`text-xs md:text-sm font-semibold tracking-tight leading-snug ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText} mb-2 truncate-2-lines overflow-hidden 
            ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilySans}`}
          >
            {product.title}
          </h3>

          {/* Price */}
          <div className="mb-3 h-[46px] flex flex-col justify-between overflow-hidden">
            <div className="leading-tight">
              <Price
                amount={salePrice}
                className={`text-sm font-bold ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText} whitespace-nowrap`}
              />
            </div>

            <div className="flex items-center gap-1.5 min-h-[16px] leading-tight flex-wrap">
              {hasDiscount && comparePrice && (
                <>
                  <Price
                    amount={comparePrice}
                    className="text-[11px] text-slate-400 line-through whitespace-nowrap"
                  />

                  <span className="text-[11px] font-bold text-red-500 whitespace-nowrap">
                    -{Math.round(((comparePrice - salePrice) / comparePrice) * 100)}%
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Variant Selector Dropdown */}

<div
  className="mb-3 min-h-[44px]"
  onClick={(e) => e.stopPropagation()}
>
  {product.variantStatus &&
    product.variants &&
    product.variants.length > 0 && (
      <SelectField
        name={`variant-select-${product.id}`}
        value={selectedVariant?.id ? String(selectedVariant.id) : ""}
        onChange={(val) => {
          const vId = Number(val);
          const found = product.variants?.find((v) => v.id === vId);
          if (found) {
            setSelectedVariant(found);
          }
        }}
        options={product.variants.map((v) => {
          const optLabel =
            v.variantAttributes
              ?.map((attr) => attr.attribute_value.name)
              .join(" / ") || `Option ${v.id}`;

          return {
            label: optLabel,
            value: String(v.id),
          };
        })}
        placeholder="Select Option"
      />
    )}
</div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={selectedStock <= 0}
          className={`w-full border transition-colors py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 ${HEADER_V2_DESIGN_SYSTEM.borderRadius.btn} ${
            selectedStock <= 0
              ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
              : `border-${HEADER_V2_DESIGN_SYSTEM.colors.neutralBorder} bg-white hover:${HEADER_V2_DESIGN_SYSTEM.colors.lightGrayBg} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText}`
          }`}
        >
          {selectedStock <= 0 ? (
            <span>Out of Stock</span>
          ) : (
            <>
              <ShoppingCart className="w-3.5 h-3.5" />
              Add to cart
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductCardV2;
